import Collection from './Collection.js'

/**
 * Does basically the same things as php version with the same class name.
 */
class SlotsParser {
  static MIN_TIME = 7 // minimum hour
  static MAX_TIME = 21 // maximum hour
  data = [] // local data
  slotMinutes = 30 // the number of minutes one slots is equal to

  constructor(data) {
      if (!data instanceof Collection) {
          this.data = new Collection(data)
      } else {
          this.data = data
      }
  }

  /**
   * Getter for minimum hour.
   *
   * @returns {number}
   */
  get minTime() {
      return this.constructor.MIN_TIME
  }

  /**
   * Getter for maximum hour.
   *
   * @returns {number}
   */
  get maxTime() {
      return this.constructor.MAX_TIME
  }

  /**
   * Getter for number of minutes per slot.
   *
   * @returns {number}
   */
  get slotInMinutes() {
      return this.slotMinutes
  }

  /**
   * Helpers for better initialization of the class.
   *
   * @param data
   * @returns {SlotsParser}
   */
  static use(data) {
      return new this(data)
  }

  /**
   * Change default number of minutes per slot.
   *
   * @param val
   * @returns {SlotsParser}
   */
  setSlotsMinutes(val) {
      this.slotMinutes = val

      return this
  }

  /**
   * Converts slots to time.
   *
   * @param data
   * @param slotsPerChunk number of slots in each chunk
   * @param strict used to control whatever the number of slots in each chunk must be exact as $slots_per_chunk
   * @returns {*}
   */
  toTime(data = null, slotsPerChunk = null, strict = false) {
      const slots = data || _.get(this.data.first(), 'slots')

      if (!slots) {
          console.warn('Slots are empty.')
          return
      }

      // get chunks of consecutive slots
      // e.g. [1,2,5,6] => [[1,2], [5,6]]
      let chunks = this.toChunks(slots, slotsPerChunk)

      if (strict) {
          chunks = chunks.filter((chunk) => chunk.length === slotsPerChunk)
      }

      chunks = chunks.map((slots) => this.convertSlots(slots))

      return chunks
  }

  /**
   * Updates collection with converted slots to times.
   *
   * @returns {*}
   */
  toTimeCollection() {
      return this.data
          .map((item) => {
              item.times = this.toTime(item.slots)
              return item
          })
          .all()
  }

  /**
   * Converts time to slots.
   *
   * @param data
   * @param delimiter
   * @returns {*}
   */
  toSlots(data = null, delimiter = ' - ') {
      const times = data || _.get(this.data.first(), 'times')

      const chunks = times.split(delimiter)

      return this.convertTimes(chunks)
  }

  /**
   * Chunks slots subsequently - e.g. to [[1, 2, 3], [6, 7, 8], [11, 12, 13]] given slots are structured like [1, 2, 3, 6, 7, 8, 11, 12, 13].
   *
   * @param slots
   * @param slotsPerChunk maximum number of slots per chunk
   * @returns {*}
   */
  toChunks(slots, slotsPerChunk = null) {
      return _.sortBy(slots).reduce((chunks, slot) => {
          if (!chunks.length) return [[slot]]

          let found = false

          chunks.forEach((chunked_slots, key) => {
              if (_.last(chunked_slots) + 1 === slot && (!slotsPerChunk || chunks[key].length < slotsPerChunk)) {
                  chunks[key].push(slot)
                  found = true
              }
          })

          if (!found) {
              chunks.push([slot])
          }

          return chunks
      }, [])
  }

  /**
   * Counts slots from collection of items.
   *
   * @returns {number}
   */
  countSlots() {
      let count = 0

      this.data.each((item) => {
          count += item.slots.length
      })

      return count
  }

  /**
   * Counts duration from slots. Returns duration in hours.
   *
   * @returns {number}
   */
  getDuration() {
      return (this.countSlots() * this.slotInMinutes) / 60
  }

  /**
   * Converts slots to times (moment instance).
   *
   * @param slots
   * @returns {moment.Moment[]}
   */
  convertSlots(slots) {
      return [
          this.getMinTimeslotTime().add((_.first(slots) - 1) * this.slotInMinutes, 'minutes'),
          this.getMinTimeslotTime().add(_.last(slots) * this.slotInMinutes, 'minutes')
      ]
  }

  /**
   * Converts times to slots.
   *
   * @param startTime
   * @param endTime
   * @returns {Array}
   */
  convertTimes([startTime, endTime]) {
      const minTime = this.getMinTimeslotTime()

      const firstSlot = moment(startTime, 'HH:mm').diff(minTime, 'minutes') / this.slotInMinutes + 1
      const lastSlot = moment(endTime, 'HH:mm').diff(minTime, 'minutes') / this.slotInMinutes

      return _.range(firstSlot, lastSlot + 1)
  }

  /**
   * Returns moment instance set to the minimum hour.
   *
   * @returns {moment.Moment}
   */
  getMinTimeslotTime() {
      return this.getDate().set('hour', this.minTime)
  }

  /**
   * Returns carbon instance set to the maximum hour.
   *
   * @returns {moment.Moment}
   */
  getMaxTimeslotTime() {
      return this.getDate().set('hour', this.maxTime)
  }

  /**
   * Returns carbon instance to start of day.
   *
   * @returns {moment.Moment}
   */
  getDate() {
      return moment().startOf('day')
  }
}

export default SlotsParser
